<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="sci-page-title-only">SIGFROTA</h1>
            <p class="text-sm text-slate-500">Controle e gerenciamento centralizado de {{ isMachinePage ? 'máquinas' : 'veículos' }}</p>
          </div>
        </div>
      </div>

      <!-- ÁREA DE FILTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-magnify</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  FILTRAR CONSULTA
              </h2>
          </div>
          <div class="p-8">
              <v-form @submit.prevent="loadVehicles" class="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                  <div class="md:col-span-10">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Selecione a Prefeitura</label>
                      <v-autocomplete
                        v-model="filter.prefecture_id"
                        :items="prefectures"
                        :readonly="!isAdmin"
                        item-text="prefecture_social_name"
                        item-value="prefecture_id"
                        placeholder="Selecione..."
                        solo flat dense hide-details
                        class="bg-slate-50 border border-slate-200 rounded-lg custom-select-main"
                        height="44"
                      ></v-autocomplete>
                  </div>
                  <div class="md:col-span-2">
                      <button 
                        @click="loadVehicles"
                        type="button"
                        class="w-full bg-[#f37021] hover:bg-[#e0601a] text-white font-bold text-[11px] h-11 rounded-lg shadow-sm transition-all uppercase tracking-widest"
                      >
                        FILTRAR
                      </button>
                  </div>
              </v-form>
          </div>
      </section>
      
      <!-- KPIS -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- KPI TOTAL -->
        <div class="bg-slate-900 rounded-2xl p-5 relative overflow-hidden shadow-xl group transition-all hover:-translate-y-1">
          <div class="absolute top-[-10%] right-[-5%] opacity-10 group-hover:opacity-20 transition-opacity">
            <v-icon size="80" color="white">mdi-car-multiple</v-icon>
          </div>
          <div class="relative space-y-4">
            <div class="flex items-center gap-2">
              <span class="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Frota Total</span>
            </div>
            <div class="flex items-baseline gap-1">
              <h3 class="text-3xl font-black text-white tracking-tighter">{{ totalVehicles }}</h3>
              <span class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{{ isMachinePage ? 'Máquinas' : 'Veículos' }}</span>
            </div>
            <div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
              <v-icon color="slate-400" x-small>mdi-chart-line</v-icon>
              <span class="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Geral da plataforma</span>
            </div>
          </div>
        </div>

        <!-- KPI ATIVOS -->
        <div class="bg-emerald-600 rounded-2xl p-5 relative overflow-hidden shadow-xl group transition-all hover:-translate-y-1">
          <div class="absolute top-[-10%] right-[-5%] opacity-10 group-hover:opacity-20 transition-opacity">
            <v-icon size="80" color="white">mdi-check-circle</v-icon>
          </div>
          <div class="relative space-y-4">
            <div class="flex items-center gap-2">
              <span class="text-[9px] font-black text-white/70 uppercase tracking-[0.2em]">Operacionais</span>
            </div>
            <div class="flex items-baseline gap-1">
              <h3 class="text-3xl font-black text-white tracking-tighter">{{ totalActive }}</h3>
              <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest">Ativos</span>
            </div>
            <div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/10 border border-white/10">
              <v-icon color="white" x-small>mdi-shield-check</v-icon>
              <span class="text-[8px] font-bold text-white uppercase tracking-wider">Prontos para uso</span>
            </div>
          </div>
        </div>

        <!-- KPI INATIVOS -->
        <div class="bg-rose-600 rounded-2xl p-5 relative overflow-hidden shadow-xl group transition-all hover:-translate-y-1">
          <div class="absolute top-[-10%] right-[-5%] opacity-10 group-hover:opacity-20 transition-opacity">
            <v-icon size="80" color="white">mdi-alert-circle</v-icon>
          </div>
          <div class="relative space-y-4">
            <div class="flex items-center gap-2">
              <span class="text-[9px] font-black text-white/70 uppercase tracking-[0.2em]">Fora de Uso</span>
            </div>
            <div class="flex items-baseline gap-1">
              <h3 class="text-3xl font-black text-white tracking-tighter">{{ totalInactive }}</h3>
              <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest">Inativos</span>
            </div>
            <div class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/10 border border-white/10">
              <v-icon color="white" x-small>mdi-alert-octagon</v-icon>
              <span class="text-[8px] font-bold text-white uppercase tracking-wider">Requer atenção</span>
            </div>
          </div>
        </div>
      </div>

      <!-- GRID DE VEÍCULOS -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div style="background-color: #333333; display: flex; align-items: center; padding: 10px 20px; width: 100%; box-sizing: border-box;">

              <!-- TÍTULO -->
              <div style="display: flex; align-items: center; gap: 8px; flex-shrink: 0;">
                <v-icon color="orange" small>{{ isMachinePage ? 'mdi-train-variant' : 'mdi-car' }}</v-icon>
                <span style="color: white; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em;">{{ isMachinePage ? 'MÁQUINAS' : 'VEÍCULOS' }}</span>
              </div>

              <!-- AÇÕES FIXADAS À DIREITA -->
              <div style="margin-left: auto; display: flex; align-items: center; gap: 12px; flex-shrink: 0;">

                <!-- QR CODE -->
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" @click="printQrCodes" small>
                      <v-icon color="white" size="18">mdi-qrcode-scan</v-icon>
                    </v-btn>
                  </template>
                  <span>Imprimir QrCodes</span>
                </v-tooltip>

                <!-- IMPRIMIR SENHAS -->
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" @click="printPasswords" small>
                      <v-icon color="white" size="18">mdi-printer-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Imprimir Senhas</span>
                </v-tooltip>

                <!-- HISTÓRICO -->
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" small>
                      <v-icon color="white" size="18">mdi-history</v-icon>
                    </v-btn>
                  </template>
                  <span>Histórico</span>
                </v-tooltip>

                <!-- BOTÃO NOVO -->
                <button
                  v-if="isAdmin || isPrefecture"
                  @click="$router.push(isMachinePage ? '/machines-manager/create' : '/vehicles-manager/create')"
                  style="height: 32px; width: auto; white-space: nowrap; flex-shrink: 0; background-color: #f37021; color: white; display: inline-flex; align-items: center; gap: 6px; border-radius: 8px; padding: 0 14px; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; border: none; cursor: pointer; transition: background-color 0.2s;"
                  onmouseover="this.style.backgroundColor='#e0601a'"
                  onmouseout="this.style.backgroundColor='#f37021'"
                >
                  <v-icon color="white" x-small>mdi-plus</v-icon>
                  NOVO
                </button>

                <!-- LUPA -->
                <v-icon
                  color="white"
                  size="18"
                  style="opacity: 0.7; cursor: pointer;"
                  @click="showSearch = !showSearch; $nextTick(() => { if (showSearch) { const el = window.document.getElementById('vehicle-search-input'); if(el) el.focus(); } })"
                >
                  {{ showSearch ? 'mdi-close' : 'mdi-magnify' }}
                </v-icon>

              </div>
          </div>

          <!-- BARRA DE PESQUISA EXPANSÍVEL (abaixo do header) -->
          <div v-if="showSearch" class="px-5 py-2 flex items-center gap-2" style="background-color: #3d3d3d;">
            <v-icon color="white" size="14" class="opacity-50">mdi-magnify</v-icon>
            <input
              v-model="search"
              type="text"
              placeholder="PESQUISAR POR PLACA, MODELO, SECRETARIA..."
              class="bg-transparent text-white w-full text-[10px] font-bold tracking-widest uppercase outline-none placeholder:text-white/30"
              @keyup.esc="showSearch = false"
              id="vehicle-search-input"
            />
          </div>


          <div class="overflow-x-auto">

             <v-data-table
              :headers="headers"
              :items="data"
              :search="search"
              :custom-filter="filterSearch"
              :loading="loader"
              :items-per-page="15"
              loading-text="CARREGANDO FROTA..."
              class="custom-table"
            >
              <template v-slot:item.vehicle_active="{ item }">
                <span 
                  :class="item.vehicle_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'" 
                  class="px-2 py-1 rounded text-[10px] font-bold uppercase"
                >
                  {{ item.vehicle_active ? 'Ativo' : 'Inativo' }}
                </span>
              </template>

              <template v-slot:item.model_desc="{ item }">
                <div class="flex flex-col py-1">
                  <span class="text-xs font-bold text-slate-700 uppercase tracking-tight">{{ item.model_desc }}</span>
                  <span class="text-[10px] font-black text-orange-500 tracking-widest uppercase">{{ item.vehicle_board }}</span>
                </div>
              </template>

              <template v-slot:item.control_type="{ item }">
                <v-chip 
                  x-small 
                  :color="item.vehicle_hodometro ? 'blue-grey lighten-5' : 'amber lighten-5'"
                  :text-color="item.vehicle_hodometro ? 'blue-grey darken-3' : 'amber darken-4'"
                  class="font-black text-[9px] uppercase px-2"
                >
                  <v-icon x-small left>{{ item.vehicle_hodometro ? 'mdi-speedometer' : 'mdi-clock-outline' }}</v-icon>
                  {{ item.vehicle_hodometro ? 'Hodômetro' : 'Horímetro' }}
                </v-chip>
              </template>

              <template v-slot:item.vehicle_reports="{ item }">
                <v-icon small :color="item.vehicle_reports ? 'emerald' : 'rose'">
                  {{ item.vehicle_reports ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
              </template>

              <template v-slot:item.action="{ item }">
                 <div class="flex justify-end gap-1">
                    <v-tooltip bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <button v-bind="attrs" v-on="on" @click="addFuelAuthorization(item)" class="p-1.5 rounded hover:bg-slate-100 text-blue-600 transition-colors">
                          <v-icon small color="currentColor">mdi-gas-station</v-icon>
                        </button>
                      </template>
                      <span>Autorizar</span>
                    </v-tooltip>
                    <v-tooltip bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <button v-bind="attrs" v-on="on" @click="loadAbastecimento(item)" class="p-1.5 rounded hover:bg-slate-100 text-slate-500 transition-colors">
                          <v-icon small color="currentColor">mdi-history</v-icon>
                        </button>
                      </template>
                      <span>Histórico</span>
                    </v-tooltip>
                    <v-tooltip bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <button v-bind="attrs" v-on="on" @click="printQrCode(item)" class="p-1.5 rounded hover:bg-slate-100 text-slate-500 transition-colors">
                          <v-icon small color="currentColor">mdi-qrcode</v-icon>
                        </button>
                      </template>
                      <span>QrCode</span>
                    </v-tooltip>
                    <v-tooltip bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <button v-bind="attrs" v-on="on" @click="resetPassword(item.vehicle_id)" class="p-1.5 rounded hover:bg-slate-100 text-amber-600 transition-colors">
                          <v-icon small color="currentColor">mdi-lock-reset</v-icon>
                        </button>
                      </template>
                      <span>Senha</span>
                    </v-tooltip>
                    <v-tooltip v-if="isAdmin || isPrefecture" bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <button v-bind="attrs" v-on="on" @click="$router.push(isMachinePage ? `/machines-manager/edit/${item.vehicle_id}` : `/vehicles-manager/edit/${item.vehicle_id}`)" class="p-1.5 rounded hover:bg-slate-100 text-orange-600 transition-colors">
                          <v-icon small color="currentColor">mdi-pencil</v-icon>
                        </button>
                      </template>
                      <span>Editar</span>
                    </v-tooltip>
                    <v-tooltip v-if="isAdmin || isPrefecture" bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <button v-bind="attrs" v-on="on" @click="deleteItem(item.vehicle_id)" class="p-1.5 rounded hover:bg-slate-100 text-red-600 transition-colors">
                          <v-icon small color="currentColor">mdi-delete</v-icon>
                        </button>
                      </template>
                      <span>Excluir</span>
                    </v-tooltip>
                 </div>
              </template>
             </v-data-table>
          </div>
      </section>

      <div style="display:none">
        <passCard :data="printData" />
        <passCardList :data="data" />
        <passQrCode :data="printDataQrCode" />
        <passQrCodeList :data="data" />
      </div>

      <!-- MODAL AUTORIZAÇÃO -->
      <v-dialog v-model="showModal" max-width="500px" persistent>
        <v-card class="rounded-xl overflow-hidden">
          <v-toolbar color="#333333" dark flat>
            <v-icon color="orange" small class="mr-2">mdi-gas-station</v-icon>
            <h3 class="text-[11px] font-bold uppercase tracking-widest">{{modalTitle}}</h3>
            <v-spacer />
            <v-btn icon small @click="showModal = false"><v-icon small>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-8 px-8">
            <v-form ref="formMotorista" v-model="validForm" lazy-validation>
              <v-row>
                <v-col cols="12">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Fornecedor</label>
                  <v-autocomplete
                    :items="providers"
                    item-text="provider_name"
                    item-value="provider_id"
                    v-model="dataForm.provider_id"
                    @change="loadItems(dataForm.provider_id,dataForm.vehicle_id,dataForm.prefecture_id)"
                    :rules="fieldRules"
                    solo flat dense
                    class="bg-slate-50 border border-slate-200 rounded-lg custom-select-main"
                  />
                </v-col>
                <v-col cols="12">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Combustível</label>
                  <v-autocomplete
                    :items="items"
                    item-text="item_description_desc"
                    item-value="item_id"
                    v-model="dataForm.item_id"
                    :rules="fieldRules"
                    solo flat dense
                    class="bg-slate-50 border border-slate-200 rounded-lg custom-select-main"
                  />
                </v-col>
                <v-col cols="12">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Motorista</label>
                  <v-autocomplete
                    :items="drivers"
                    item-text="driver_name"
                    item-value="driver_id"
                    v-model="dataForm.driver_id"
                    append-icon="mdi-account-plus"
                    @click:append="showFormMotorista = true"
                    :rules="fieldRules"
                    solo flat dense
                    class="bg-slate-50 border border-slate-200 rounded-lg custom-select-main"
                  />
                </v-col>
                <v-col cols="6">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Lts Autorizados</label>
                  <v-text-field
                    v-model="dataForm.supply_authorized_liters"
                    v-mask-decimal.br="3"
                    :disabled="disableLitros"
                    solo flat dense
                    class="bg-slate-50 border border-slate-200 rounded-lg custom-select-main"
                  />
                </v-col>
                <v-col cols="6">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Valor (R$)</label>
                  <v-text-field
                    v-model="dataForm.supply_authorized_value"
                    v-mask-decimal.br="2"
                    :disabled="disableValor"
                    solo flat dense
                    class="bg-slate-50 border border-slate-200 rounded-lg custom-select-main"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions class="pb-8 px-8">
            <v-spacer />
            <v-btn text color="slate-500" class="text-[10px] font-bold uppercase tracking-widest" @click="showModal = false">Cancelar</v-btn>
            <v-btn color="orange" dark class="px-8 text-[10px] font-bold uppercase tracking-widest rounded-lg shadow-lg" @click="createSupplyAuthorization(dataForm)">Confirmar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- MODAL NOVO MOTORISTA -->
      <v-dialog v-model="showFormMotorista" width="400">
        <v-card class="rounded-xl overflow-hidden">
          <v-toolbar color="#333333" dark flat>
            <v-icon color="orange" small class="mr-2">mdi-account-plus</v-icon>
            <h3 class="text-[11px] font-bold uppercase tracking-widest">CADASTRAR MOTORISTA</h3>
          </v-toolbar>
          <v-card-text class="pt-8 px-8">
            <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Nome Completo</label>
            <v-text-field 
              v-model="driver_name" 
              solo flat dense 
              class="bg-slate-50 border border-slate-200 rounded-lg custom-select-main"
            />
          </v-card-text>
          <v-card-actions class="pb-8 px-8">
            <v-spacer />
            <v-btn color="orange" dark class="px-8 text-[10px] font-bold uppercase tracking-widest rounded-lg" @click="newDriver">Salvar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- MODAL HISTÓRICO -->
      <v-dialog v-model="showModalAbastecimento" width="900">
        <v-card class="rounded-xl overflow-hidden">
          <v-toolbar color="#333333" dark flat>
            <v-icon color="orange" small class="mr-2">mdi-history</v-icon>
            <h3 class="text-[11px] font-bold uppercase tracking-widest">{{modalTitleAbastecimento}}</h3>
            <v-spacer />
            <v-btn icon small @click="showModalAbastecimento = false"><v-icon small>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="p-0">
            <v-data-table
              :headers="headersAbastecimento"
              :items="abastecimentoData"
              :loading="loaderAbastecimento"
              loading-text="CARREGANDO HISTÓRICO..."
              class="custom-table"
            >
              <template v-slot:item.supply_authorized_value="{ item }">
                <span class="font-bold text-slate-700">R$ {{realMask(safeToFixed(item.supply_authorized_value, 2))}}</span>
              </template>
              <template v-slot:item.datehour="{ item }">
                <span class="text-xs text-slate-500">{{convertDateTime(item.datehour)}}</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar
        multi-line
        :color="snack.color"
        v-model="snack.show"
        transition="fade-transition"
        class="custom-snack"
      >
        <div class="flex items-center gap-2">
          <v-icon small color="white">{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
          <span class="text-xs font-bold uppercase tracking-widest">{{ snack.message }}</span>
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn dark text v-bind="attrs" @click="snack.show = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import passCard from "./passCard";
import passCardList from "./listPassCards";
import passQrCode from "./passQrCode";
import passQrCodeList from "./listPassQrCodes";
import Helpers from "@/mixins/helpers.js";
export default {
  props: ["id"],
  components: {
    passCard,
    passCardList,
    passQrCode,
    passQrCodeList,
  },
  mixins: [Helpers],
  data() {
    return {
      search: "",
      loader: true,
      loaderAbastecimento: false,
      showModalAbastecimento: false,
      modalTitleAbastecimento: "Autorizações de Abastecimento",
      abastecimentoData: [],
      showSearch: false,
      data: [],
      printData: [],
      printDataQrCode: [],
      prefectures: [],
      filter: {
        prefecture_id: null,
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      headers: [
        { text: "Secretaria / Sub-Setor", value: "sec_name" },
        { text: "Modelo / Placa", value: "model_desc" },
        { text: "Controle", value: "control_type", align: "center" },
        { text: "Combustível", value: "fuel_desc" },
        { text: "Status", value: "vehicle_active", align: "center" },
        { text: "Relatorio", value: "vehicle_reports", align: "center" },
        { text: "Senha", value: "vehicle_password", align: "center" },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
      headersAbastecimento: [
        { text: "Data e Hora", value: "datehour", align: "start" },
        { text: "Motorista", value: "driver_name" },
        { text: "Fornecedor", value: "provider_name" },
        { text: "Item", value: "item_description_desc" },
        { text: "Lts Aut.", value: "supply_authorized_liters", align: "center" },
        { text: "Valor Aut.", value: "supply_authorized_value", align: "center" },
        { text: "Status", value: "supply_status", align: "center" }
      ],
      showModal: false,
      showFormMotorista: false,
      loadingModal: false,
      loadingMotorista: false,
      loadingSubmit: false,
      validForm: false,
      providers: [],
      drivers: [],
      items: [],
      driver_name: "",
      fieldRules: [(v) => !!v || "Campo obrigatório"],
      modalTitle: "Autorizar Abastecimento",
      dataForm: {
        supply_authorized_liters: "0",
        supply_authorized_value: "0",
        supply_status: "PRE-AUTORIZADO",
        supply_adjusted: false,
        supply_extra: false,
        supply_liters_stocked: false,
        supply_billed: false,
        vehicle_id: null,
        driver_id: null,
        item_id: null,
        prefecture_id: null,
        provider_id: null,
        is_autorized: true,
        authorizer: this.$store.getters.getUserData.user_login,
      },
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    disableLitros() {
      return parseFloat(this.dataForm.supply_authorized_value.replace(',', '.')) > 0;
    },
    disableValor() {
      return parseFloat(this.dataForm.supply_authorized_liters.replace(',', '.')) > 0;
    },
    isAdmin() {
      return this.$store.getters.getUserData.group_id === 1;
    },
    isPrefecture() {
      return this.$store.getters.getUserData.table_key === 'prefectures';
    },
    totalVehicles() {
      return this.data.length;
    },
    totalActive() {
      return this.data.filter(v => v.vehicle_active).length;
    },
    totalInactive() {
      return this.data.filter(v => !v.vehicle_active).length;
    },
    isMachinePage() {
      return this.$route.path.includes('machines-manager');
    },
  },
  methods: {
    async addFuelAuthorization(item) {
      this.clearForm();
      this.modalTitle = `Autorizar Abastecimento - Veículo: ${item.vehicle_board}`;
      this.showModal = true;
      this.loadingModal = true;
      const driverRequest = this.$http.get(`/supplies/drivers/${item.prefecture_id}`);
      const providerRequest = this.$http.get(`/providers/prefecture/${item.prefecture_id}`);
      const [driversResponse, providersResponse] = await Promise.all([driverRequest, providerRequest]);
      this.drivers = driversResponse.data;
      this.providers = providersResponse.data;
      this.dataForm.vehicle_id = item.vehicle_id;
      this.dataForm.prefecture_id = item.prefecture_id;
      this.dataForm.provider_id = this.providers.length > 0 ? this.providers[0].provider_id : null;
      if (this.dataForm.provider_id) await this.loadItems(this.dataForm.provider_id, item.vehicle_id, item.prefecture_id);
      this.loadingModal = false;
    },
    async loadItems(provider_id, vehicle_id, prefecture_id) {
      const res = await this.$http.get(`/supplies/providers/${provider_id}/${vehicle_id}/${prefecture_id}`);
      this.items = res.data;
      this.dataForm.item_id = this.items.length > 0 ? this.items[0].item_id : null;
    },
    clearForm() {
      this.dataForm = {
        supply_authorized_liters: "0",
        supply_authorized_value: "0",
        supply_status: "PRE-AUTORIZADO",
        supply_adjusted: false,
        supply_extra: false,
        supply_liters_stocked: false,
        supply_billed: false,
        vehicle_id: null,
        driver_id: null,
        item_id: null,
        prefecture_id: null,
        provider_id: null,
        is_autorized: true,
        authorizer: this.$store.getters.getUserData.user_login,
      };
      this.providers = [];
      this.items = [];
      this.drivers = [];
    },
    newDriver() {
      this.loadingMotorista = true;
      this.$http.post("/supplies/drivers", { driver_name: this.driver_name, prefecture_id: this.dataForm.prefecture_id }).then((r) => {
        this.snack.show = true;
        this.snack.message = r.data.result;
        this.snack.color = "success";
        this.$http.get(`/supplies/drivers/${this.dataForm.prefecture_id}`).then((d) => {
          this.drivers = d.data;
          const driverItems = d.data.filter((f) => f.driver_name === this.driver_name ? f : null);
          this.dataForm.driver_id = driverItems[0].driver_id;
          this.loadingMotorista = false;
          this.showFormMotorista = false;
          this.driver_name = '';
        });
      }).catch((e) => {
        this.snack.show = true;
        this.snack.color = "error";
        this.snack.message = e.message;
        this.loadingMotorista = false;
      });
    },
    async createSupplyAuthorization(data) {
      if (this.$refs.formMotorista.validate()) {
        try {
          this.loadingSubmit = true;
          await this.$http.post("/supplies/single", data);
          this.snack.show = true;
          this.snack.message = "Autorização de abastecimento criada com sucesso!";
          this.snack.color = "success";
          this.loadingSubmit = false;
          this.showModal = false;
          this.clearForm();
        } catch (error) {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = error.message;
          this.loadingSubmit = false;
        }
      }
    },
    printPasswords() { this.$htmlToPaper("printCards"); },
    printCard(d) { this.printData = d; setTimeout(() => this.$htmlToPaper("printMe"), 1000); },
    printQrCodes() { this.$htmlToPaper("printQrCodes"); },
    printQrCode(d) { this.printDataQrCode = d; setTimeout(() => this.$htmlToPaper("printMeQrCode"), 1000); },
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.get("/prefectures").then((r) => {
        this.prefectures = r.data;
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
        if (this.$store.getters.getUserData.table_key === 'prefectures' && this.$store.getters.getUserData.table_id) {
          this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
          this.loadVehicles();
        } else if (this.$store.getters.getUserData.group_id !== 1) {
          this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
          this.loadVehicles();
        } else if (this.id) {
          this.filter.prefecture_id = this.id;
          this.loadVehicles();
        }
      }).catch(() => {
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      });
    },
    loadVehicles() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.get(`/vehicles/prefectures/${this.filter.prefecture_id}`).then((r) => {
        let filtered = r.data;
        if (!(this.isAdmin || this.isPrefecture)) {
          filtered = filtered.filter(v => v.vehicle_active === true);
        }

        if (this.isMachinePage) {
          this.data = filtered.filter(v => !!v.vehicle_horimetro);
        } else {
          this.data = filtered.filter(v => !!v.vehicle_hodometro);
        }

        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      }).catch((e) => {
        this.loader = false;
        this.snack.show = true;
        this.snack.message = e;
        this.snack.type = "error";
        this.$store.dispatch("setLoader", this.loader);
      });
    },
    deleteItem(id) {
      if (confirm("Deseja excluir o registro?")) {
        this.$store.dispatch("setLoader", true);
        this.$http.delete(`/vehicles/${id}`).then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.loadVehicles();
        }).catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", false);
        });
      }
    },
    resetPassword(id) {
      if (confirm("Deseja resetar a senha do veículo?")) {
        this.$store.dispatch("setLoader", true);
        this.$http.patch(`/vehicles/reset/password/${id}`).then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.loadVehicles();
        }).catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", false);
        });
      }
    },
    async loadAbastecimento(vehicle) {
      this.loaderAbastecimento = true;
      this.abastecimentoData = [];
      this.modalTitleAbastecimento = `Autorizações de Abastecimento - Veículo: ${vehicle.vehicle_board}`;
      this.showModalAbastecimento = true;
      const filter = { prefecture_id: vehicle.prefecture_id, vehicle_id: vehicle.vehicle_id, all_status: true, authorizer: this.$store.getters.getUserData.user_login, is_autorized: true };
      const res = await this.$http.put("/supplies/billings", filter);
      this.abastecimentoData = res.data;
      this.loaderAbastecimento = false;
    },
    safeToFixed(value, digits = 2) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return Number(0).toFixed(digits);
      return numericValue.toFixed(digits);
    },
    filterSearch(value, search, item) {
      if (!search) return true;
      const term = search.toString().toLowerCase().trim();
      
      const board = item.vehicle_board ? item.vehicle_board.toString().toLowerCase() : "";
      const model = item.model_desc ? item.model_desc.toString().toLowerCase() : "";
      const brand = item.brand_desc ? item.brand_desc.toString().toLowerCase() : "";
      const sec = item.sec_name ? item.sec_name.toString().toLowerCase() : "";
      const password = item.vehicle_password ? item.vehicle_password.toString().toLowerCase() : "";
      
      return board.includes(term) || 
             model.includes(term) || 
             brand.includes(term) || 
             sec.includes(term) ||
             password.includes(term);
    },
  },
};
</script>

<style scoped>
.sci-page-wrapper {
  background-color: #f8fafc;
  min-height: 100vh;
  padding: 80px 20px;
}
.sci-page-title-only {
  font-size: 1.5rem;
  font-weight: 900;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: -0.025em;
}

.custom-select-main >>> .v-input__control .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
}
.custom-select-main >>> input {
  text-transform: uppercase;
  font-weight: 600;
  font-size: 0.8rem;
  color: #334155 !important;
}

/* Custom Table Override */
.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep thead { background-color: #f8fafc !important; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.7rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 56px !important;
  padding: 0 24px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  height: 64px !important;
  padding: 0 24px !important;
  text-transform: uppercase;
}

.custom-snack >>> .v-snack__wrapper {
  border-radius: 12px !important;
}
</style>
